import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { SharedFilterValues } from "./SharedFilters"
import ExportButton from "./ExportButton"
import reportsService from "../../services/reportsService"
import { formatCentsWithSymbol } from "../../utils/formatCurrency"
import { useTranslation } from "react-i18next"

interface CollectionsTabProps {
    filters: SharedFilterValues
    companyId: string
}

const fmtC = (v: number, c: string = "EUR") => formatCentsWithSymbol(v, c)

const CollectionsTab: React.FC<CollectionsTabProps> = ({
    filters,
    companyId,
}) => {
    const { t } = useTranslation()
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(false)

    const loadData = useCallback(async () => {
        if (!companyId) return
        setLoading(true)
        try {
            const result = await reportsService.getCollectionsReport(
                companyId,
                {
                    outletId: filters.outletId || undefined,
                    registerId: filters.registerId || undefined,
                    dateFrom: filters.dateFrom || undefined,
                    dateTo: filters.dateTo || undefined,
                }
            )
            setData(result)
        } catch (err) {
            console.error("Failed to load collections:", err)
        } finally {
            setLoading(false)
        }
    }, [companyId, filters])

    useEffect(() => {
        loadData()
    }, [loadData])

    const collections = data?.collections || []
    const totalTransactions = data?.total_transactions || 0
    const grandTotal = data?.grand_total || 0

    return (
        <Box>
            {loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : collections.length > 0 ? (
                <>
                    <Paper
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            gap: 3,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.collectionsTab.summary.totalTransactions"
                                )}
                                :
                            </strong>{" "}
                            {totalTransactions}
                        </Typography>
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.collectionsTab.summary.totalAmount"
                                )}
                                :
                            </strong>{" "}
                            {fmtC(grandTotal)}
                        </Typography>
                        <Box sx={{ ml: "auto" }}>
                            <ExportButton
                                companyId={companyId}
                                reportType="collections"
                                filters={filters}
                            />
                        </Box>
                    </Paper>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.collectionsTab.columns.paymentMethod"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.collectionsTab.columns.type"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t(
                                            "exportsComponents.collectionsTab.columns.transactions"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t(
                                            "exportsComponents.collectionsTab.columns.amount"
                                        )}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {collections.map((c: any, i: number) => (
                                    <TableRow key={i} hover>
                                        <TableCell>
                                            {c.payment_method_name}
                                        </TableCell>
                                        <TableCell>
                                            {c.payment_method_type}
                                        </TableCell>
                                        <TableCell align="right">
                                            {c.transaction_count}
                                        </TableCell>
                                        <TableCell align="right">
                                            {fmtC(
                                                c.total_amount || 0,
                                                c.currency
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            ) : (
                <Box>
                    <Paper
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <ExportButton
                            companyId={companyId}
                            reportType="collections"
                            filters={filters}
                        />
                    </Paper>
                    <Paper sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("exportsComponents.collectionsTab.empty")}
                        </Typography>
                    </Paper>
                </Box>
            )}
        </Box>
    )
}

export default CollectionsTab
